import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';
import './RegisterPage.css';
import { refGuardado, olvidaRef } from '../utils/afiliado.js';
import { enlaceWhatsApp } from '../contacto.js';
import { LIMITES, avisaDelTope } from '../limites.js';
import { versionesLegales } from '../components/legal/useLegalPendiente.js';
import { sesionDelDiagnostico } from '../state/onboardingStore.js';
import { mensajeDeError } from '../utils/errores.js';

// ── LA ZONA HORARIA DEL NAVEGADOR ─────────────────────────────────────────
//  Sustituye a mandarle la IP del alumno a un servicio de fuera. Devuelve algo
//  como «America/Mexico_City» o «Europe/Madrid», y el servidor deduce de ahí el
//  país y si esa persona está bajo el RGPD (`src/zona_del_alumno.py`).
function zonaHoraria() {
    try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;
    } catch {
        return undefined;   // navegador muy viejo: se registra igual, sin país
    }
}

const waSupport = `¡Hola! Tengo problemas para crear mi cuenta de Spacy.\n\n¿Me pueden ayudar?`;

// El número lo pone `contacto.js`. Estaba escrito aquí además de allí, y se le
// pasaba a mano al helper justo para saltarse ese valor por defecto: el día que
// cambie el número, esta pantalla se habría quedado con el viejo.
const openWhatsApp = (msg) => {
    window.open(enlaceWhatsApp(msg), '_blank', 'noopener');
};

// ── EL CONTADOR QUE NO ESTORBA ──────────────────────────────────────────────
//  Nadie escribiendo su nombre se acerca a 50 caracteres, así que un contador
//  siempre visible sería ruido en las tres cajas a la vez. Pero un tope MUDO es
//  peor: al llegar al límite el teclado simplemente deja de responder y no hay
//  nada que explique por qué.
//
//  Así que aparece al 80% —el mismo umbral que usa el resto de NSL, en
//  `avisaDelTope`— y solo en la caja que se está acercando. Con «Roberto
//  Rivera» no se ve nada; con un nombre larguísimo avisa antes de chocar.
function Contador({ valor, tope }) {
    if (!avisaDelTope(valor.length, tope)) return null;
    const lleno = valor.length >= tope;
    return (
        <p className={`rp-contador ${lleno ? 'rp-contador-lleno' : ''}`} aria-live="polite">
            {lleno
                ? `Máximo ${tope} caracteres`
                : `${valor.length} / ${tope}`}
        </p>
    );
}

const WaIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
    </svg>
);

function RegisterPage() {
    const [fullName, setFullName] = useState('');
    const [alias, setAlias] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [errors, setErrors] = useState({});
    const [passwordStrength, setPasswordStrength] = useState(0);
    const [theme, setTheme] = useState(() => localStorage.getItem('theme') || 'light');

    // ── LO QUE ACEPTA AL CREAR LA CUENTA (LEGAL.md, L4) ──────────────────
    // La casilla manda las VERSIONES que están publicadas, no un «sí»: así la
    // firma dice qué texto leyó esta persona. Si los documentos no cargan, la
    // casilla sigue funcionando y el servidor se lo preguntará al entrar.
    const [acepto, setAcepto] = useState(false);
    const [versiones, setVersiones] = useState({});

    // ── Y LA EDAD, EN SU PROPIA CASILLA (28-09-2026) ─────────────────────
    // Separada a propósito. Meter la declaración de edad dentro del «acepto
    // los términos» es precisamente lo que las autoridades de protección de
    // datos reprochan: dos consentimientos distintos agrupados en un clic, de
    // forma que no se puede demostrar que la persona reparó en el segundo.
    //
    // Rob tenía la política escrita desde el 17-09 —NSL es para mayores, y un
    // menor solo con permiso de sus padres— pero no se preguntaba en ninguna
    // parte: un niño se registraba y nadie se enteraba.
    const [mayorEdad, setMayorEdad] = useState(false);

    useEffect(() => {
        document.documentElement.setAttribute('data-theme', theme);
        document.body.setAttribute('data-theme', theme);
        localStorage.setItem('theme', theme);
    }, [theme]);

    useEffect(() => {
        let vivo = true;
        versionesLegales().then((v) => { if (vivo) setVersiones(v); });
        return () => { vivo = false; };
    }, []);

    const navigate = useNavigate();
    const toggleTheme = () => setTheme(prev => (prev === 'light' ? 'dark' : 'light'));

    const checkPasswordStrength = (pass) => {
        let strength = 0;
        if (pass.length > 7) strength++;
        if (pass.match(/([a-z].*[A-Z])|([A-Z].*[a-z])/)) strength++;
        if (pass.match(/([0-9])/)) strength++;
        if (pass.match(/([!,%,&,@,#,$,^,*,?,_,~])/)) strength++;
        setPasswordStrength(strength);
    };

    const handlePasswordChange = (e) => {
        const newPassword = e.target.value;
        setPassword(newPassword);
        checkPasswordStrength(newPassword);
    };

    const validateForm = () => {
        const newErrors = {};
        if (!fullName.trim()) newErrors.fullName = 'El nombre es requerido.';
        else if (fullName.length > LIMITES.nombreCompleto)
            newErrors.fullName = `El nombre no puede pasar de ${LIMITES.nombreCompleto} caracteres.`;
        if (!alias.trim()) newErrors.alias = 'El alias es requerido.';
        else if (alias.length > LIMITES.alias)
            newErrors.alias = `El alias no puede pasar de ${LIMITES.alias} caracteres.`;
        if (!email || !/\S+@\S+\.\S+/.test(email)) newErrors.email = 'Email inválido.';
        // El `maxLength` del input no cubre el autocompletado del navegador ni
        // un pegado por teclado en algunos móviles. El servidor rechaza con un
        // 422 que esta pantalla traduciría a un error genérico, así que se dice
        // aquí, señalando el campo.
        else if (email.length > LIMITES.correo)
            newErrors.email = `El correo no puede pasar de ${LIMITES.correo} caracteres.`;
        if (passwordStrength < 4) newErrors.password = 'La contraseña es muy débil.';
        if (password !== confirmPassword) newErrors.confirmPassword = 'Las contraseñas no coinciden.';
        if (!acepto) newErrors.acepto = 'Para crear tu cuenta acepta los términos y el aviso de privacidad.';
        if (!mayorEdad) newErrors.mayorEdad = 'Confirma tu edad para crear la cuenta.';
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (!validateForm()) return;
        setIsLoading(true);
        setErrors({});
        try {
            // Inyectamos la URL base dinámica
            const API_BASE_URL = import.meta.env.VITE_API_URL || '';
            const response = await fetch(`${API_BASE_URL}/api/register`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    email, password,
                    full_name: fullName,
                    alias,
                    // El codigo del profesor que lo trajo, si lo hubo. Va el
                    // TEXTO, nunca un identificador de profesor: quien esta
                    // detras lo resuelve el servidor.
                    ref: refGuardado() || undefined,
                    // Las versiones que acepta la casilla. El servidor las
                    // compara con las suyas antes de crear la cuenta.
                    acepta_terminos: versiones.terminos,
                    acepta_privacidad: versiones.privacidad,
                    acepta_edad: versiones.edad,
                    // El diagnóstico que acaba de hacer, si pasó por la puerta:
                    // con esto el servidor recupera sus respuestas y no se las
                    // vuelve a pedir al entrar.
                    diagnostico: sesionDelDiagnostico() || undefined,
                    // ── DE DÓNDE ES, SIN MANDARLE LA IP A NADIE ─────────────
                    //  Hasta el 30-09-2026 el servidor le mandaba la IP del
                    //  alumno a un servicio gratuito de fuera para deducir su
                    //  país. Una IP es un dato personal, y aquel servicio era
                    //  el único proveedor de NSL sin ningún contrato.
                    //
                    //  El navegador ya sabe la zona horaria y la dice sin
                    //  preguntarle a nadie. Con eso basta para lo único que
                    //  hace falta: saber si esta persona está bajo el RGPD y
                    //  hay que atenderla con el proveedor de IA que sí tiene
                    //  contrato.
                    //
                    //  Dentro de un `try` porque en un navegador muy viejo
                    //  `Intl` puede no estar: sin zona, la cuenta se crea
                    //  igual y se queda sin país, que es lo que ya pasaba
                    //  cuando el servicio de IP no contestaba.
                    zona_horaria: zonaHoraria(),
                }),
            });
            if (response.ok) {
                // El codigo ya cumplio. Si se quedara guardado, el siguiente
                // que se registrara en este mismo navegador —un hermano, un
                // companero— quedaria atribuido al profesor sin que nadie le
                // hubiera pasado nada.
                olvidaRef();
                navigate('/verify-email', { state: { email } });
            } else {
                const errorData = await response.json();
                // Manejo de error de alias duplicado
                if (response.status === 409 && errorData.detail?.suggestions) {
                    setErrors({ alias: `${errorData.detail.message} ${errorData.detail.suggestions.join(', ')}` });
                } else {
                    setErrors({ api: mensajeDeError(
                        response, errorData.detail,
                        'No pudimos crear tu cuenta. Inténtalo de nuevo en un momento.') });
                }
            }
        } catch {
            setErrors({ api: 'No se pudo conectar al servidor.' });
        } finally {
            setIsLoading(false);
        }
    };
    
    return (
        <div className="rp-page">
            <div className="rp-container">
                <div className="rp-theme-toggle-container">
                    <button onClick={toggleTheme} className="rp-theme-toggle-btn">
                        {theme === 'light' ? '🌙' : '☀️'}
                    </button>
                </div>
                <h1 className="rp-title">Crear una Cuenta</h1>
                
                <form onSubmit={handleSubmit} noValidate className="rp-form">
                    <div className="rp-form-row">
                        <div className="rp-input-group">
                           <input type="text" className="rp-input-field" placeholder="Nombre Completo" value={fullName} onChange={(e) => setFullName(e.target.value)} autoComplete="name" maxLength={LIMITES.nombreCompleto} required />
                           <Contador valor={fullName} tope={LIMITES.nombreCompleto} />
                           {errors.fullName && <p className="rp-error-message">{errors.fullName}</p>}
                        </div>
                        <div className="rp-input-group">
                           <input type="text" className="rp-input-field" placeholder="Alias" value={alias} onChange={(e) => setAlias(e.target.value)} autoComplete="nickname" maxLength={LIMITES.alias} required />
                           <Contador valor={alias} tope={LIMITES.alias} />
                           {errors.alias && <p className="rp-error-message">{errors.alias}</p>}
                        </div>
                    </div>

                    <div className="rp-input-group">
                        <input type="email" className="rp-input-field" placeholder="Correo Electrónico" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" maxLength={LIMITES.correo} required />
                        <Contador valor={email} tope={LIMITES.correo} />
                        {errors.email && <p className="rp-error-message">{errors.email}</p>}
                    </div>

                    <div className="rp-input-group">
                        <div className="rp-input-wrapper">
                            <input type={showPassword ? 'text' : 'password'} className="rp-input-field" placeholder="Contraseña" value={password} onChange={handlePasswordChange} autoComplete="new-password" required />
                            <button type="button" className="rp-icon-button" onClick={() => setShowPassword(!showPassword)}>
                                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                            </button>
                        </div>
                        <div className="rp-password-strength-meter">
                           <div className={`rp-strength-bar ${passwordStrength > 0 ? 'filled' : ''}`} data-strength={passwordStrength > 2 ? 'strong' : (passwordStrength > 1 ? 'medium' : 'weak')}></div>
                           <div className={`rp-strength-bar ${passwordStrength > 1 ? 'filled' : ''}`} data-strength={passwordStrength > 2 ? 'strong' : (passwordStrength > 1 ? 'medium' : 'weak')}></div>
                           <div className={`rp-strength-bar ${passwordStrength > 2 ? 'filled' : ''}`} data-strength="strong"></div>
                           <div className={`rp-strength-bar ${passwordStrength > 3 ? 'filled' : ''}`} data-strength="strong"></div>
                        </div>
                        {errors.password ? <p className="rp-error-message">{errors.password}</p> : <p className={`rp-password-requirements ${passwordStrength >= 4 ? 'met' : ''}`}>Debe incluir mayúsculas, minúsculas, números y símbolos.</p>}
                    </div>

                    <div className="rp-input-group">
                        <input type="password" className="rp-input-field" placeholder="Confirmar Contraseña" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} autoComplete="new-password" required />
                        {errors.confirmPassword && <p className="rp-error-message">{errors.confirmPassword}</p>}
                    </div>

                    <label className="rp-legal" data-prueba="acepta-legal-registro">
                        <input type="checkbox" checked={acepto} onChange={(e) => setAcepto(e.target.checked)} />
                        <span>
                            He leído y acepto los{' '}
                            <Link to="/terminos" target="_blank" rel="noopener">Términos y condiciones</Link>
                            {' '}y el{' '}
                            <Link to="/privacidad" target="_blank" rel="noopener">Aviso de privacidad</Link>.
                        </span>
                    </label>
                    {errors.acepto && <p className="rp-error-message">{errors.acepto}</p>}

                    <label className="rp-legal" data-prueba="acepta-edad-registro">
                        <input type="checkbox" checked={mayorEdad}
                               onChange={(e) => setMayorEdad(e.target.checked)} />
                        <span>
                            Soy mayor de edad. Si soy menor, mi padre, madre o tutor
                            lo sabe, lo autoriza y supervisa mi entrenamiento.
                        </span>
                    </label>
                    {errors.mayorEdad && <p className="rp-error-message">{errors.mayorEdad}</p>}

                    {errors.api && <p className="rp-error-message api-error">{errors.api}</p>}
                    <button type="submit" className="rp-register-btn"
                            disabled={isLoading || !acepto || !mayorEdad}>
                        {isLoading ? 'Creando cuenta...' : 'Crear Cuenta'}
                    </button>
                </form>
            </div>

            {/* ── Support Footer ── */}
            <footer className="rp-support-footer">
                <div className="rp-support-card">
                    <div className="rp-support-icon">🛟</div>
                    <div className="rp-support-copy">
                        <strong>¿Problemas con tu acceso?</strong>
                        <span>Soporte disponible de lunes a sábado</span>
                    </div>
                    <button className="rp-wa-ghost" type="button" onClick={() => openWhatsApp(waSupport)}>
                        <WaIcon />
                        Contactar
                    </button>
                </div>

                <div className="rp-footer-links">
                    <span className="rp-footer-text">¿Ya tienes una cuenta?</span>
                    <Link to="/login" className="rp-footer-link">Inicia sesión aquí</Link>
                </div>
            </footer>
        </div>
    );
}

export default RegisterPage;